import React, { useState, useRef } from 'react';
import { SaveIcon, XIcon, FileTextIcon, AlertTriangleIcon, CheckIcon, UploadIcon } from 'lucide-react';
import { ExchangeRate, ExchangeRateType, EntityScope } from '../CurrencyExchangeView';
interface ExchangeRateUploadProps {
  onImport: (rates: ExchangeRate[]) => void;
  onCancel: () => void;
  locale: string;
}
export function ExchangeRateUpload({
  onImport,
  onCancel,
  locale
}: ExchangeRateUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [mappings, setMappings] = useState({
    fromCurrency: '',
    toCurrency: '',
    rate: '',
    type: '',
    scope: '',
    isActive: ''
  });
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<ExchangeRate[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [step, setStep] = useState<'upload' | 'map' | 'preview'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    // Check file type
    const fileType = selectedFile.type;
    if (fileType !== 'text/csv' && fileType !== 'application/vnd.ms-excel' && !fileType.includes('spreadsheetml')) {
      setErrors(['Unsupported file format. Please upload a CSV or Excel file.']);
      return;
    }
    // Store document type information with the file
    const fileWithType = {
      file: selectedFile,
      documentType: 'Exchange Rate Data',
      name: selectedFile.name,
      size: selectedFile.size,
      uploadDate: new Date().toISOString()
    };
    // For this demo, we'll just parse CSV files
    // In a real application, you would use a library like xlsx for Excel files
    if (fileType === 'text/csv') {
      const reader = new FileReader();
      reader.onload = event => {
        try {
          const csvData = event.target?.result as string;
          const lines = csvData.split('\n');
          const headers = lines[0].split(',').map(header => header.trim());
          setAvailableColumns(headers);
          const data = [];
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim() === '') continue;
            const values = lines[i].split(',').map(value => value.trim());
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index];
            });
            data.push(row);
          }
          setParsedData(data);
          setStep('map');
          setErrors([]);
        } catch (error) {
          setErrors(['Failed to parse CSV file. Please check the format.']);
        }
      };
      reader.readAsText(selectedFile);
    } else {
      // For demo purposes, we'll show a message for Excel files
      setErrors(['Excel file parsing is not implemented in this demo. Please use CSV format.']);
    }
  };
  const handleMappingChange = (field: string, column: string) => {
    setMappings({
      ...mappings,
      [field]: column
    });
  };
  const validateAndPreview = () => {
    // Validate required mappings
    if (!mappings.fromCurrency || !mappings.toCurrency || !mappings.rate) {
      setErrors(['From Currency, To Currency, and Rate mappings are required.']);
      return;
    }
    try {
      // Convert parsed data to ExchangeRate format
      const convertedData: ExchangeRate[] = parsedData.map((row, index) => {
        const rateValue = parseFloat(row[mappings.rate]);
        if (isNaN(rateValue)) {
          throw new Error(`Invalid rate value at row ${index + 1}: ${row[mappings.rate]}`);
        }
        return {
          id: `import-${Date.now()}-${index}`,
          fromCurrency: row[mappings.fromCurrency],
          toCurrency: row[mappings.toCurrency],
          rate: rateValue,
          inverseRate: 1 / rateValue,
          type: mappings.type && row[mappings.type] ? row[mappings.type] as ExchangeRateType : 'Manual',
          scope: mappings.scope && row[mappings.scope] ? row[mappings.scope] as EntityScope : 'Global',
          isActive: mappings.isActive ? row[mappings.isActive] === 'true' || row[mappings.isActive] === 'TRUE' || row[mappings.isActive] === '1' : true,
          lastUpdated: new Date().toISOString(),
          updatedBy: 'Import'
        };
      });
      // Validate data
      const validationErrors: string[] = [];
      convertedData.forEach((rate, index) => {
        if (!rate.fromCurrency) {
          validationErrors.push(`Row ${index + 1}: Missing From Currency`);
        }
        if (!rate.toCurrency) {
          validationErrors.push(`Row ${index + 1}: Missing To Currency`);
        }
        if (isNaN(rate.rate) || rate.rate <= 0) {
          validationErrors.push(`Row ${index + 1}: Invalid Rate value`);
        }
      });
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }
      setPreviewData(convertedData);
      setStep('preview');
      setErrors([]);
    } catch (error) {
      setErrors([error.message || 'Failed to process data. Please check your mappings.']);
    }
  };
  const handleImport = () => {
    onImport(previewData);
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-7839">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-7840">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7841">
          Import Exchange Rates
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7842">
          <XIcon className="h-5 w-5" data-id="element-7843" />
        </button>
      </div>
      <div className="p-6 space-y-6" data-id="element-7844">
        {/* Step indicator */}
        <div className="flex items-center justify-center mb-6" data-id="element-7845">
          <div className={`flex items-center ${step === 'upload' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-7846">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step === 'upload' ? 'bg-indigo-100' : 'bg-gray-100'}`} data-id="element-7847">
              <UploadIcon className="h-4 w-4" data-id="element-7848" />
            </div>
            <span className="ml-2 text-sm font-medium" data-id="element-7849">Upload File</span>
          </div>
          <div className="h-1 w-8 bg-gray-200 mx-2" data-id="element-7850"></div>
          <div className={`flex items-center ${step === 'map' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-7851">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step === 'map' ? 'bg-indigo-100' : 'bg-gray-100'}`} data-id="element-7852">
              <FileTextIcon className="h-4 w-4" data-id="element-7853" />
            </div>
            <span className="ml-2 text-sm font-medium" data-id="element-7854">Map Fields</span>
          </div>
          <div className="h-1 w-8 bg-gray-200 mx-2" data-id="element-7855"></div>
          <div className={`flex items-center ${step === 'preview' ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-7856">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step === 'preview' ? 'bg-indigo-100' : 'bg-gray-100'}`} data-id="element-7857">
              <CheckIcon className="h-4 w-4" data-id="element-7858" />
            </div>
            <span className="ml-2 text-sm font-medium" data-id="element-7859">Preview & Import</span>
          </div>
        </div>
        {/* Error messages */}
        {errors.length > 0 && <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4" data-id="element-7860">
            <div className="flex" data-id="element-7861">
              <div className="flex-shrink-0" data-id="element-7862">
                <AlertTriangleIcon className="h-5 w-5 text-red-400" data-id="element-7863" />
              </div>
              <div className="ml-3" data-id="element-7864">
                <h3 className="text-sm font-medium text-red-800" data-id="element-7865">
                  There{' '}
                  {errors.length === 1 ? 'is an issue' : `are ${errors.length} issues`}{' '}
                  with your data
                </h3>
                <div className="mt-2 text-sm text-red-700" data-id="element-7866">
                  <ul className="list-disc pl-5 space-y-1" data-id="element-7867">
                    {errors.map((error, index) => <li key={index} data-id="element-7868">{error}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>}
        {/* Upload step */}
        {step === 'upload' && <div data-id="element-7869">
            <div className="flex items-center justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-7870">
              <div className="space-y-1 text-center" data-id="element-7871">
                <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-7872" />
                <div className="flex text-sm text-gray-600" data-id="element-7873">
                  <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-7874">
                    <span data-id="element-7875">Upload a file</span>
                    <input id="file-upload" name="file-upload" type="file" className="sr-only" ref={fileInputRef} onChange={handleFileUpload} accept=".csv,.xls,.xlsx" data-id="element-7876" />
                  </label>
                  <p className="pl-1" data-id="element-7877">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500" data-id="element-7878">
                  CSV or Excel file with exchange rate data
                </p>
              </div>
            </div>
            <div className="mt-6" data-id="element-7879">
              <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-7880">
                Expected format:
              </h4>
              <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-7881">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-7882">
                  <thead data-id="element-7883">
                    <tr data-id="element-7884">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7885">
                        From Currency
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7886">
                        To Currency
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7887">
                        Rate
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7888">
                        Type (Optional)
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7889">
                        Scope (Optional)
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7890">
                        Is Active (Optional)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-7891">
                    <tr data-id="element-7892">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7893">
                        USD
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7894">
                        EUR
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7895">
                        0.92
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7896">
                        Manual
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7897">
                        Global
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7898">
                        TRUE
                      </td>
                    </tr>
                    <tr data-id="element-7899">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7900">
                        EUR
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7901">
                        GBP
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7902">
                        0.87
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7903">
                        Manual
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7904">
                        Global
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7905">
                        TRUE
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-gray-500" data-id="element-7906">
                * Header names in your file can be different - you'll map them
                in the next step
              </p>
            </div>
          </div>}
        {/* Mapping step */}
        {step === 'map' && <div data-id="element-7907">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-7908">
              Map your file columns to exchange rate fields
            </h4>
            <div className="space-y-4" data-id="element-7909">
              <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2" data-id="element-7910">
                <div data-id="element-7911">
                  <label htmlFor="fromCurrency" className="block text-sm font-medium text-gray-700" data-id="element-7912">
                    From Currency *
                  </label>
                  <select id="fromCurrency" value={mappings.fromCurrency} onChange={e => handleMappingChange('fromCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required data-id="element-7913">
                    <option value="" data-id="element-7914">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-7915">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-7916">
                  <label htmlFor="toCurrency" className="block text-sm font-medium text-gray-700" data-id="element-7917">
                    To Currency *
                  </label>
                  <select id="toCurrency" value={mappings.toCurrency} onChange={e => handleMappingChange('toCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required data-id="element-7918">
                    <option value="" data-id="element-7919">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-7920">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-7921">
                  <label htmlFor="rate" className="block text-sm font-medium text-gray-700" data-id="element-7922">
                    Rate *
                  </label>
                  <select id="rate" value={mappings.rate} onChange={e => handleMappingChange('rate', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" required data-id="element-7923">
                    <option value="" data-id="element-7924">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-7925">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-7926">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-7927">
                    Type (Optional)
                  </label>
                  <select id="type" value={mappings.type} onChange={e => handleMappingChange('type', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7928">
                    <option value="" data-id="element-7929">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-7930">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-7931">
                  <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-7932">
                    Scope (Optional)
                  </label>
                  <select id="scope" value={mappings.scope} onChange={e => handleMappingChange('scope', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7933">
                    <option value="" data-id="element-7934">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-7935">
                        {column}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-7936">
                  <label htmlFor="isActive" className="block text-sm font-medium text-gray-700" data-id="element-7937">
                    Is Active (Optional)
                  </label>
                  <select id="isActive" value={mappings.isActive} onChange={e => handleMappingChange('isActive', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7938">
                    <option value="" data-id="element-7939">Select column</option>
                    {availableColumns.map(column => <option key={column} value={column} data-id="element-7940">
                        {column}
                      </option>)}
                  </select>
                </div>
              </div>
              {parsedData.length > 0 && <div className="mt-6" data-id="element-7941">
                  <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-7942">
                    Data preview (first 5 rows):
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-7943">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-7944">
                      <thead data-id="element-7945">
                        <tr data-id="element-7946">
                          {availableColumns.map(column => <th key={column} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7947">
                              {column}
                            </th>)}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-7948">
                        {parsedData.slice(0, 5).map((row, rowIndex) => <tr key={rowIndex} data-id="element-7949">
                            {availableColumns.map(column => <td key={column} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7950">
                                {row[column]}
                              </td>)}
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-xs text-gray-500" data-id="element-7951">
                    Showing {Math.min(5, parsedData.length)} of{' '}
                    {parsedData.length} rows
                  </p>
                </div>}
            </div>
          </div>}
        {/* Preview step */}
        {step === 'preview' && <div data-id="element-7952">
            <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-7953">
              Review and confirm import ({previewData.length} exchange rates)
            </h4>
            <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-7954">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-7955">
                <thead className="bg-gray-50" data-id="element-7956">
                  <tr data-id="element-7957">
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7958">
                      From
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7959">
                      To
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7960">
                      Rate
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7961">
                      Inverse
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7962">
                      Type
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7963">
                      Scope
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7964">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-7965">
                  {previewData.slice(0, 10).map((rate, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-7966">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-7967">
                        {rate.fromCurrency}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7968">
                        {rate.toCurrency}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7969">
                        {rate.rate.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7970">
                        {rate.inverseRate.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7971">
                        {rate.type}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7972">
                        {rate.scope}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-7973">
                        {rate.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-7974">
                            Active
                          </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-7975">
                            Inactive
                          </span>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {previewData.length > 10 && <p className="mt-2 text-xs text-gray-500" data-id="element-7976">
                Showing 10 of {previewData.length} exchange rates
              </p>}
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4" data-id="element-7977">
              <div className="flex" data-id="element-7978">
                <div className="flex-shrink-0" data-id="element-7979">
                  <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-7980" />
                </div>
                <div className="ml-3" data-id="element-7981">
                  <p className="text-sm text-yellow-700" data-id="element-7982">
                    Importing these rates will update any existing rates with
                    the same currency pairs. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>}
        {/* Action buttons */}
        <div className="pt-5 border-t border-gray-200" data-id="element-7983">
          <div className="flex justify-end space-x-3" data-id="element-7984">
            {step !== 'upload' && <button type="button" onClick={() => setStep(step === 'preview' ? 'map' : 'upload')} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7985">
                Back
              </button>}
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7986">
              Cancel
            </button>
            {step === 'upload' && file && <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7987">
                Change File
              </button>}
            {step === 'map' && <button type="button" onClick={validateAndPreview} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7988">
                <CheckIcon className="h-4 w-4 mr-2" data-id="element-7989" />
                Continue
              </button>}
            {step === 'preview' && <button type="button" onClick={handleImport} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7990">
                <SaveIcon className="h-4 w-4 mr-2" data-id="element-7991" />
                Import Rates
              </button>}
          </div>
        </div>
      </div>
    </div>;
}